/**
 * @file SDK context and provider for the Guide Generator UI.
 *
 * Provides authenticated API clients for both Payload CMS and Mosaic API,
 * with automatic API key validation and role checking.
 */

import type { FC, ReactNode } from "react";

import { createApiSdk } from "@mosaicdb/client/api/sdk";
import { PayloadSDK } from "@mosaicdb/client/payload/sdk";
import { useDebounce } from "@reactuses/core";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useState } from "react";

import type { AccessRole } from "@/lib/core/payload/utils/Access";

/**
 * Create SDK state with API key management and authentication.
 *
 * Debounces API key input to avoid excessive validation requests.
 * Validates that the user has required roles (GuideGenerator, Reader).
 *
 * Used by the SDK provider component to manage its internal state.
 *
 * @returns SDK state including API clients and authentication status.
 */
const useSdkState = () => {
  const [apiKeyRaw, setApiKey] = useState(``);
  const apiKey = useDebounce(apiKeyRaw, 1000, { leading: false });
  const sdk = useMemo(
    () => ({
      api: createApiSdk({
        baseUrl: ``,
        headers: { Authorization: `users API-Key ${apiKey}` },
      }),
      payload: new PayloadSDK({
        baseInit: { headers: { Authorization: `users API-Key ${apiKey}` } },
        baseURL: `/api`,
      }),
    }),
    [apiKey],
  );
  const auth = useQuery({
    queryFn: async () => {
      if (apiKeyRaw.length === 0) {
        return null;
      }
      const response = await sdk.api.GET(`/api/~/auth/me`);

      if (!response.data) {
        throw new Error(
          `Authentication failed: ${response.response.statusText}`,
        );
      }

      if (!response.data.roleList.includes(`Root`)) {
        for (const role of [
          `GuideGenerator`,
          `Reader`,
        ] satisfies AccessRole[]) {
          if (!response.data.roleList.includes(role)) {
            throw new Error(
              `Your account does not have the ${role} role. Please contact an administrator.`,
            );
          }
        }
      }

      return response.data;
    },
    queryKey: [`auth`, apiKey],
  });

  return useMemo(
    () => ({
      apiKey: apiKeyRaw,
      auth,
      sdk,
      setApiKey,
    }),
    [apiKeyRaw, auth, sdk, setApiKey],
  );
};
type UseSdkValue = ReturnType<typeof useSdkState>;

const SdkContext = createContext<null | UseSdkValue>(null);

/**
 * Provider component for SDK context.
 *
 * Wrap components that need access to the Mosaic API clients.
 */
export const SdkProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const sdk = useSdkState();
  return <SdkContext.Provider value={sdk}>{children}</SdkContext.Provider>;
};

/**
 * Access the SDK context for API calls.
 *
 * @returns SDK state with API clients and authentication info.
 * @throws Error if used outside of SdkProvider.
 */
export const useSdk = () => {
  const sdk = useContext(SdkContext);
  if (!sdk) {
    throw new Error(`SDK not initialized`);
  }
  return sdk;
};
